import { FeatureName } from 'v5/platform/feature-management/feature-management';
import { Permission } from '../PermissionService/PermissionService';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import { Maybe, RemoteData } from '@passionware/monads';
import { AppScopeIdentity } from 'v5/core/app-scope/appScope.types';

/**
 * Capabilities that also require the named Labs flag. `{ action }` looks this
 * up — call sites do not pass a feature.
 */
export const featureFlaggedPemissions = {
  productions: [
    Permission.PRODUCTION_READ,
    Permission.PRODUCTION_CREATE,
    Permission.PRODUCTION_UPDATE,
    Permission.PRODUCTION_ARCHIVE,
    Permission.CONFIGURE_PRODUCTION_TYPES,
    Permission.CONFIGURE_PRODUCTION_TEMPLATES,
    Permission.CONFIGURE_ALERTS,
    Permission.CONFIGURE_SLOT_CATEGORIES
  ],
  shortlists: [
    Permission.SHORTLIST_READ,
    Permission.SHORTLIST_CREATE,
    Permission.SHORTLIST_UPDATE,
    Permission.SHORTLIST_ARCHIVE,
    Permission.SHORTLIST_PUBLISH
  ],
  callouts: [
    Permission.CALLOUT_READ,
    Permission.CALLOUT_CREATE,
    Permission.CALLOUT_UPDATE,
    Permission.CALLOUT_ARCHIVE,
    Permission.CALLOUT_PUBLISH
  ],
  callsheets: [
    Permission.CALLSHEET_READ,
    Permission.CALLSHEET_CREATE,
    Permission.CALLSHEET_UPDATE,
    Permission.CALLSHEET_ARCHIVE,
    Permission.CALLSHEET_SEND,
    Permission.CONFIGURE_CALLSHEET_TEMPLATES
  ],
  bookings: [
    Permission.BOOKING_READ,
    Permission.BOOKING_CREATE,
    Permission.BOOKING_UPDATE,
    Permission.BOOKING_ARCHIVE,
    Permission.CONFIGURE_BOOKING_TYPES
  ],
  resources: [
    Permission.RESOURCE_READ,
    Permission.RESOURCE_CREATE,
    Permission.RESOURCE_UPDATE,
    Permission.RESOURCE_ARCHIVE,
    Permission.CONFIGURE_RESOURCE_TYPES,
    Permission.MANAGE_RESOURCE_DUPLICATES,
    Permission.CONFIGURE_RATE_CARDS
  ],
  booking_confirmation: [
    Permission.BOOKING_SEND_CONFIRMATION,
    Permission.CONFIGURE_BOOKING_CONFIRMATION_TEMPLATES
  ],
  reporting: [
    Permission.REPORT_DOWNLOAD,
    Permission.CONFIGURE_REPORT_TEMPLATES
  ],
  reconciliations: [Permission.MANAGE_RECONCILIATION],
  'custom-nikefrontdesk': [Permission.CONFIGURE_CUSTOM_NIKE_FRONT_DESK],
  'custom-nikearrivalboard': [Permission.CONFIGURE_CUSTOM_NIKE_ARRIVAL_BOARD],
  'custom-nikeqrcode': [Permission.CONFIGURE_CUSTOM_NIKE_QR_CODE],
  'custom-splashlight': [Permission.DOCUSIGN_SEND]
} satisfies Partial<
  Record<FeatureName<AtellioFeatureGroupDefinition>, Array<Permission>>
>;

// Pre-computed reverse mapping for O(1) lookup
export const actionToFeatureMap = new Map<
  Permission,
  FeatureName<AtellioFeatureGroupDefinition>
>();

// Build the reverse mapping once at module load time
Object.entries(featureFlaggedPemissions).forEach(([feature, permissions]) => {
  permissions.forEach(permission => {
    actionToFeatureMap.set(
      permission,
      feature as FeatureName<AtellioFeatureGroupDefinition>
    );
  });
});

type FeaturesWithPermissions = keyof typeof featureFlaggedPemissions;

type FeaturesWithoutPermissions = Exclude<
  FeatureName<AtellioFeatureGroupDefinition>,
  FeaturesWithPermissions
>;

export const specialCapabilities = ['manage_feature_flags'] as const;

export type SpecialCapability = (typeof specialCapabilities)[number];

export type FeatureQuery = {
  feature: FeaturesWithoutPermissions;
};
export type ActionQuery = { action: Permission | SpecialCapability };

export type AccessQuery =
  /**
   * AccessQuery:
   * - Use `{ feature }` when you want to check access for a feature that does not require a specific permission.
   *   These are features that are only controlled by feature flags and do not have associated permissions.
   *   Example: `{ feature: 'booking-statistics' }`
   *   Do not use this for features with mapped permissions (resources, productions, …) — it throws.
   *
   * - Use `{ action }` when you want to check a permission. If the action is in
   *   `featureFlaggedPemissions`, the related feature flag is AND-ed automatically.
   *   Example: `{ action: Permission.PRODUCTION_CREATE }`
   *
   * The `{ action }` path automatically determines which feature flag (if any) is associated with
   * the given action and checks both the flag and the permission for access.
   */
  FeatureQuery | ActionQuery;
export interface AccessService {
  /**
   * Hook returning a reactive function to check if user has access
   * Combines feature flag and permission logic
   */
  useCan: (
    scope: Maybe<AppScopeIdentity>
  ) => (access: AccessQuery) => RemoteData<boolean>;
  /**
   * `useCan` for a list of identities — one hook, no hooks-in-a-loop.
   * Pass team identities for team dests and workspace identities for workspace dests.
   */
  useCanQueries: (
    scopes: AppScopeIdentity[]
  ) => Array<(access: AccessQuery) => RemoteData<boolean>>;
  /**
   * Returns whether user has access at call time
   * Combines feature flag and permission logic
   */
  can: (scope: AppScopeIdentity, access: AccessQuery) => Promise<boolean>;
}

export interface WithAccessService {
  accessService: AccessService;
}
